import type {
  Ajustes,
  Bitacora,
  Cliente,
  Devolucion,
  Gasto,
  Lote,
  Movimiento,
  Producto,
  Usuario,
  Venta,
} from '../types'

/** Todas las tablas del sistema, en una sola foto.
 *
 *  El volumen de una distribuidora de berries cabe holgadamente en
 *  memoria (miles de filas, no millones), así que la app carga todo al
 *  entrar y trabaja en local. Eso hace que registrar una venta sea
 *  instantáneo aunque la señal en terreno sea mala; la escritura al
 *  backend ocurre después, sin bloquear la pantalla. */
export interface Snapshot {
  usuarios: Usuario[]
  productos: Producto[]
  lotes: Lote[]
  movimientos: Movimiento[]
  clientes: Cliente[]
  ventas: Venta[]
  devoluciones: Devolucion[]
  bitacora: Bitacora[]
  gastos: Gasto[]
  ajustes: Ajustes
}

export type Tabla = Exclude<keyof Snapshot, 'ajustes'>

/** Contrato mínimo que debe cumplir cualquier backend. La lógica de
 *  negocio vive en el store, no aquí: un adaptador solo guarda y lee. */
export interface Adapter {
  nombre: 'local' | 'supabase'
  /** True cuando el backend está configurado y accesible. */
  disponible(): Promise<boolean>
  cargar(): Promise<Snapshot>
  guardar<T extends Tabla>(tabla: T, filas: Snapshot[T]): Promise<void>
  eliminar(tabla: Tabla, ids: string[]): Promise<void>
  guardarAjustes(ajustes: Ajustes): Promise<void>
  /** Reemplaza todo el contenido. Se usa al restaurar un respaldo. */
  reemplazarTodo(snapshot: Snapshot): Promise<void>
}

export const AJUSTES_POR_DEFECTO: Ajustes = {
  id: 'ajustes',
  stockMinimoGlobal: 10,
  diasAvisoVencimiento: 7,
  diasCredito: 30,
  nombreEmpresa: 'NovaBerry',
  rutEmpresa: '',
  direccionEmpresa: '',
  telefonoEmpresa: '',
  emailEmpresa: '',
  moneda: 'CLP',
  porcentajeIva: 19,
  categoriasGasto: ['Arriendo', 'Sueldos', 'Combustible', 'Publicidad', 'Servicios básicos', 'Otros'],
  bodegaDireccion: 'Maroto 458, Concón',
  bodegaLat: -32.9214426,
  bodegaLng: -71.5159928,
  despachadorNombre: 'Máximo Díaz',
  despachadorTelefono: '+56932584344',
  tarifaDespachoBase: 2000,
  tarifaDespachoPorKm: 700,
}

export const snapshotVacio = (): Snapshot => ({
  usuarios: [],
  productos: [],
  lotes: [],
  movimientos: [],
  clientes: [],
  ventas: [],
  devoluciones: [],
  bitacora: [],
  gastos: [],
  ajustes: { ...AJUSTES_POR_DEFECTO },
})
